import { Button, Card, Col, Icon, Rate, Row } from 'antd'
import PropTypes from 'prop-types'
import React, { Component, Fragment } from 'react'
import { withSiteData } from 'react-static'
import styled, { withTheme } from 'styled-components'
import { Divider, Flex, StyledAnchor } from '../styles/styled-common'
import { addQuery } from '../utils'
import { symbolByCode } from '../utils/currencies'
import { LocationsCell } from './cells'
import PropItem from './PropItem'

const Img = styled.img`
  max-width: 100%;
  max-height: 100%;
`

const ProCon = styled.div`
  .anticon {
    font-size: 0.9em;
    margin-right: 6px;
  }
`

const gutter = {
  xs: 8,
  sm: 8,
  md: 24,
  lg: 48,
  xl: 48,
}

const colMarginTop = 40

const StCol = styled(Col)`
  margin-top: ${colMarginTop}px;
  ul {
    padding-left: 0;
  }
  h2 {
    margin-bottom: 8px;
  }
`

const StCard = styled(Card)`
  &.ant-card {
    color: ${p => p.theme.offBlack};
    border-color: ${p => p.theme.textOffwhite};
    overflow: hidden;

    .ant-card-cover {
      background: ${p => p.theme.darkBlue};
      padding: 20px;
      height: 140px;
    }

    .ant-card-actions {
      background: ${p => p.theme.darkBlue};
      span {
        display: none;
      }
    }

    .ant-card-body {
      background: ${p => p.theme.antLink};
      color: #fff;
    }
  }

  h1 {
    text-align: center;
    margin: 0;
  }
`

const Locations = styled(LocationsCell)`
  font-size: 0.8em;
  text-align: left;
  text-align: start;

  .bah-flag .flag-icon {
    box-shadow: 2px 2px 4px 1px #000;
    border: 1px solid rgba(255, 255, 255, 0.1);
    background-size: cover;
  }
`

const RatingScore = styled.span`
  font-size: 0.7em;
  margin-right: 5px;
  color: ${p => p.theme.textOffwhite};
`

const Rater = styled(Rate)`
  &.ant-rate-disabled .ant-rate-star {
    cursor: pointer;
  }
`

const StDivider = styled(Divider)`
  &.ant-divider {
    margin-top: 0;
    margin-bottom: 8px;
  }
`

const Table = styled.table`
  width: 100%;

  caption {
    font-family: 'Saira', 'Segoe UI', 'Monospaced Number', 'Chinese Quote',
      -apple-system, BlinkMacSystemFont, Roboto, 'PingFang SC',
      'Hiragino Sans GB', 'Microsoft YaHei', 'Helvetica Neue', Helvetica, Arial,
      sans-serif;
    caption-side: top;
    font-size: 1.5em;
    padding: 0;
    color: #fff;
    font-weight: 500;
    margin-bottom: 8px;
  }
`

class HostOverview extends Component {
  state = {
    currSymbol: '$',
  }

  static propTypes = {
    host: PropTypes.object.isRequired,
  }

  static getDerivedStateFromProps(nextProps) {
    return {
      currSymbol: symbolByCode(nextProps.host.baseCurrency),
    }
  }

  render() {
    const {
      host: {
        id,
        logo,
        name,
        ratings,
        homepageUrl,
        baseCurrency,
        established,
        pros: hostPros,
        cons: hostCons,
        promoCodes = [],
        moneyBack,
        currency,
        gameInfo: {
          packages,
          pros: gamePros,
          cons: gameCons,
          locations,
          orderUrl,
          landingPageUrl,
        },
      },
      siteData: { site },
      theme,
      ...props
    } = this.props

    const pros = [...new Set((hostPros || []).concat(gamePros || []))]

    const cons = [...new Set((hostCons || []).concat(gameCons || []))]

    const { currSymbol } = this.state

    const affiliateLink = `/go/${id}`

    const cardCover = (
      <div style={{ height: '100%' }}>
        <Flex style={{ height: '100%' }}>
          <Img
            alt={`${name} ${site.game.shortName} Server Hosting`}
            src={addQuery(logo, { h: '140' })}
          />
        </Flex>
      </div>
    )

    const cardActions = [<Fragment />]

    const pack = packages && packages.length && { ...packages[0] }

    const establishedDate =
      established &&
      new Date(established).toLocaleDateString('en-us', {
        year: 'numeric',
        month: 'long',
      })

    let moneyBackText = 'Unknown'

    if (moneyBack) {
      if (moneyBack.time < 0) moneyBackText = 'None'
      else if (moneyBack.time > 0) {
        moneyBackText = `${moneyBack.time} ${
          moneyBack.unit === 'd' ? 'Days' : 'Hours'
        }`
      }
    }

    return (
      <Row gutter={gutter} {...props}>
        <StCol xs={24} lg={12} xxl={6}>
          <a href={affiliateLink} rel="nofollow noopener">
            <StCard actions={cardActions} cover={cardCover}>
              <h1>
                {name} <br /> {site.game.shortName} Server Hosting
              </h1>
            </StCard>
          </a>
        </StCol>
        <StCol xs={24} lg={12} xxl={6}>
          {/* <StDivider orientation="left">Overview</StDivider> */}
          {/* <h2>Overview</h2> */}
          <Table>
            <caption>Overview</caption>
            <tbody>
              {established && (
                <tr>
                  <th>Established</th>
                  <td>{establishedDate}</td>
                </tr>
              )}
              <tr>
                <th>Website</th>
                <td>
                  <StyledAnchor
                    style={{ color: theme.antLink }}
                    href={affiliateLink}
                    rel="nofollow noopener"
                  >
                    {homepageUrl}
                  </StyledAnchor>
                </td>
              </tr>
              {!ratings || !ratings.score ? null : (
                <tr>
                  <th>TrustPilot Rating</th>
                  <td>
                    <a
                      href={ratings.url}
                      target="_blank"
                      rel="nofollow noreferrer noopener"
                    >
                      <Rater
                        disabled
                        allowHalf={true}
                        value={ratings.stars}
                        defaultValue={ratings.stars}
                      />
                      <RatingScore>({ratings.score}/5)</RatingScore>
                    </a>
                  </td>
                </tr>
              )}
              <tr>
                <th>Money Back Guarantee</th>
                <td>{moneyBackText}</td>
              </tr>
              <tr>
                <th>Pay Using</th>
                <td>{currency.join(', ')}</td>
              </tr>
            </tbody>
          </Table>
          {/* <StDivider orientation="left" style={{ marginTop: 20 }}>
            Locations
          </StDivider> */}
          <h2 style={{ marginTop: '20px' }}>Locations</h2>
          <Locations
            currency={baseCurrency}
            locations={locations}
            justifyContent="start"
          />
        </StCol>
        <StCol xs={24} lg={12} xxl={7}>
          <h2>Pros and Cons</h2>
          <ul>
            {pros.map((text, i) => (
              <li key={`pros${i}`}>
                <ProCon>
                  <Icon
                    type="plus-circle"
                    theme="twoTone"
                    twoToneColor="#3bb300"
                  />
                  {text}
                </ProCon>
              </li>
            ))}
            {cons.map((text, i) => (
              <li key={`cons${i}`}>
                <ProCon key={`cons${i}`}>
                  <Icon
                    type="minus-circle"
                    theme="twoTone"
                    twoToneColor="#d41f82"
                  />
                  {text}
                </ProCon>
              </li>
            ))}
          </ul>
        </StCol>
        <StCol xs={24} lg={12} xxl={4}>
          {pack && (
            <Fragment>
              {/* <h2>Example Package</h2> */}
              <Table>
                <caption>Example Package</caption>
                <tbody>
                  <tr>
                    <th>Slots</th>
                    <td>{pack.slots}</td>
                  </tr>
                  <tr>
                    <th>Price</th>
                    <td>
                      {currSymbol}
                      {pack.price.toFixed(2)} per slot
                    </td>
                  </tr>
                  <tr>
                    {/* try to guess location */}
                    <th>Location</th>
                    <td>{pack.location}</td>
                  </tr>
                  <tr>
                    <th>Total</th>
                    <td>
                      {currSymbol}
                      {(pack.slots * pack.price).toFixed(2)}
                      /mo
                    </td>
                  </tr>
                </tbody>
              </Table>
              <a href={affiliateLink} target="_blank" rel="nofollow noopener">
                <Button
                  style={{ marginTop: 10, width: '100%' }}
                  type="primary"
                  size="large"
                >
                  Buy / Customize
                </Button>
              </a>
            </Fragment>
          )}
          {!!promoCodes.length && (
            <Fragment>
              <h2 style={{ marginTop: 20 }}>Promo Codes</h2>
              {promoCodes
                .filter(
                  ({ validUntil }) =>
                    !validUntil || new Date(validUntil) > Date.now()
                )
                .map(({ title, code }) => (
                  <PropItem key={code} label={title}>
                    {code}
                  </PropItem>
                ))}
            </Fragment>
          )}
        </StCol>
      </Row>
    )
  }
}

export default withSiteData(
  withTheme(styled(HostOverview)`
    font-size: 1.1em;
    color: ${p => p.theme.textOffwhite};
    margin-top: ${-colMarginTop}px;
  `)
)



// WEBPACK FOOTER //
// /opt/build/repo/src/components/HostOverview.jsx